"use client";

import { Droplets, Sun, Waves, Wrench, Zap } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function Installationfaq() {
  const careIcons = [{
    icon: Zap,
    label: "POWER"
  }, {
    icon: Wrench,
    label: "SETUP"
  }, {
    icon: Waves,
    label: "FLOW"
  }, {
    icon: Droplets,
    label: "DRAIN"
  }, {
    icon: Sun,
    label: "TEMP"
  }];
  const rituals = [{
    question: "How do I prepare for an AC installation?",
    body: "Ensure clear access to the installation area, both indoors and outdoors. Remove furniture or obstacles near the wall units and ensure a dedicated power circuit is available for your new climate control system.",
    sub: ["Clear a 1-meter radius around the unit", "Verify electrical circuit capacity", "Ensure easy access for our technicians"]
  }, {
    question: "What happens during the site survey?",
    body: "Our engineers assess your room dimensions, insulation levels, and window orientation to calculate the exact cooling load required. This ensures your system runs efficiently without wasting energy.",
    sub: ["Room volume and heat load calculation", "Placement optimization for airflow", "Drainage and pipework routing plan"]
  }, {
    question: "How long does a standard installation take?",
    body: "A typical single-split system installation is completed within one working day. Multi-room systems or complex commercial setups may require two or more days depending on the scale of the project.",
    sub: ["Single unit: 4 to 6 hours", "Multi-split: 1 to 2 days", "Full testing and commissioning included"]
  }, {
    question: "Is my system energy efficient?",
    body: "Modern inverter-driven systems adjust their output based on real-time demand. By maintaining a steady temperature rather than cycling on and off, they significantly reduce your monthly electricity consumption.",
    sub: ["Inverter technology for steady cooling", "High SEER ratings for lower bills", "Eco-friendly refrigerant standards"]
  }, {
    question: "How do I maintain the system post-install?",
    body: "Clean the air filters every three months to ensure optimal airflow. Schedule a professional service annually to check refrigerant levels, clean the coils, and inspect the drainage system for blockages.",
    sub: ["Wash filters every 90 days", "Annual professional health check", "Keep outdoor unit free of debris"]
  }, {
    question: "What if I need a custom solution?",
    body: "We specialize in bespoke climate control for unique spaces. Whether it's a glass-walled conservatory or a server room, we design custom ducting and placement to meet your specific environmental needs.",
    sub: ["Bespoke ducting design", "Specialized server room cooling", "Architectural-friendly unit placement"]
  }, {
    question: "Are there noise considerations?",
    body: "Modern units are designed for near-silent operation. We ensure the indoor unit is mounted on vibration-dampening brackets and the outdoor unit is placed away from bedroom windows to maintain your comfort.",
    sub: ["Vibration-dampening wall mounts", "Strategic outdoor unit placement", "Whisper-quiet fan modes"]
  }, {
    question: "What is the warranty coverage?",
    body: "All our installations come with a comprehensive parts and labor warranty. We stand by our workmanship and provide ongoing support to ensure your Coastline system performs perfectly for years to come.",
    sub: ["Full parts and labor guarantee", "Priority support for registered systems", "Annual maintenance reminders"]
  }];
  return <section data-section-id="1265" id="installationfaq" className="bg-slate-50 text-slate-900 py-28 md:py-40">
      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex lg:flex-col lg:items-start gap-4 lg:gap-3 mb-10">
              <div className="flex lg:flex-col items-center lg:items-start gap-3 lg:gap-4 lg:h-32">
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600/60 lg:[writing-mode:vertical-rl] lg:rotate-180">
                  COASTLINE
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600/60 lg:[writing-mode:vertical-rl] lg:rotate-180">
                  06 — INSTALLATION & CARE
                </span>
              </div>
            </div>

            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight mb-8 text-slate-900">
              Expert <span className="text-blue-600">installation</span>,
              <br />
              perfect climate control.
            </h2>

            <p className="font-default font-normal text-base md:text-lg leading-[1.65] text-slate-600 max-w-md mb-12">
              Coastline Air Conditioning Services provides professional climate control solutions across Sussex and Surrey. We ensure every installation is precise, efficient, and built to last.
            </p>

            <div className="flex items-center gap-4 mb-10">
              <div className="h-px bg-slate-200 flex-1" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                SUSSEX & SURREY
              </span>
              <div className="w-1.5 h-1.5 rounded-md bg-blue-600" />
            </div>

            <div className="grid grid-cols-5 gap-3 md:gap-5">
              {careIcons.map((item, i) => {
              const Icon = item.icon;
              return <div key={i} data-index={i} className="flex flex-col items-start gap-3">
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-blue-600" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      {item.label}
                    </span>
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {rituals.map((ritual, i) => {
              const num = String(i + 1).padStart(2, "0");
              return <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b-0 border-t border-slate-200 first:border-t group/item data-[state=open]:border-blue-200">
                    <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden group">
                      <div className="flex items-start gap-6 md:gap-10 w-full pr-4">
                        <div className="flex items-center gap-2 shrink-0 pt-1">
                          <span className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 group-data-[state=open]:text-blue-600 transition-colors duration-300">
                            {num} / 08
                          </span>
                          <div className="w-1.5 h-1.5 rounded-md bg-blue-600 opacity-0 group-data-[state=open]:opacity-100 transition-opacity duration-300" />
                        </div>
                        <h4 className="font-default font-semibold text-lg md:text-xl leading-[1.3] text-slate-900 flex-1 text-left">
                          {ritual.question}
                        </h4>
                        <div className="relative w-4 h-4 shrink-0 mt-2">
                          <span className="absolute left-0 top-1/2 w-4 h-px bg-slate-400 -translate-y-1/2" />
                          <span className="absolute left-1/2 top-0 h-4 w-px bg-slate-400 -translate-x-1/2 transition-transform duration-300 group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0" />
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8 pt-0">
                      <div className="pl-0 md:pl-[7.5rem] pr-10 max-w-2xl">
                        <p className="font-default font-normal text-base leading-[1.65] text-slate-600 mb-6">
                          {ritual.body}
                        </p>
                        <ul className="space-y-2.5">
                          {ritual.sub.map((s, j) => <li key={j} data-index={j} className="flex items-start gap-3 font-default font-normal text-sm leading-[1.6] text-slate-500">
                              <span className="w-1.5 h-1.5 rounded-md bg-blue-600 mt-2 shrink-0" />
                              <span>{s}</span>
                            </li>)}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}